import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { complaintsService, Complaint } from '../../services/complaintsService';
import { snackActions } from '../../utils/snackbarUtils';

const statusLabels = {
  open: 'Ouvert',
  in_progress: 'En cours',
  resolved: 'Résolu',
  rejected: 'Rejeté',
  on_hold: 'En attente',
} as const;

const priorityLabels = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  critical: 'Critique',
} as const;

const statusColors = {
  open: 'primary',
  in_progress: 'info',
  resolved: 'success',
  rejected: 'error',
  on_hold: 'warning',
} as const;

const priorityColors = {
  low: 'info',
  medium: 'primary',
  high: 'warning',
  critical: 'error',
} as const;

const ComplaintDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const fetchComplaint = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await complaintsService.getComplaintById(parseInt(id));
      setComplaint(data);
    } catch (error) {
      snackActions.error('Erreur lors du chargement de la réclamation');
      console.error('Error fetching complaint:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchComplaint();
  }, [fetchComplaint]);

  const handleBack = () => {
    navigate('/reclamations');
  };

  const handleEdit = () => {
    navigate(`/reclamations/${id}/modifier`);
  };

  const handleDelete = async () => {
    if (!complaint) return;
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette réclamation ?')) {
      try {
        await complaintsService.deleteComplaint(complaint.id);
        snackActions.success('Réclamation supprimée avec succès');
        navigate('/reclamations');
      } catch (error) {
        snackActions.error('Erreur lors de la suppression de la réclamation');
      }
    }
  };

  const handleAddNote = async () => {
    if (!complaint || !newNote.trim()) return;

    try {
      setAddingNote(true);
      await complaintsService.addNote(complaint.id, newNote.trim());
      setNewNote('');
      snackActions.success('Note ajoutée avec succès');
      fetchComplaint(); // Refresh data
    } catch (error) {
      snackActions.error('Erreur lors de l\'ajout de la note');
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement de la réclamation...</Typography>
      </Box>
    );
  }

  if (!complaint) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary" mb={2}>
          Réclamation non trouvée
        </Typography>
        <Button variant="contained" onClick={handleBack}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Réclamation {complaint.reference}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ mr: 1 }}
          >
            Modifier
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Supprimer
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations Générales
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Référence
                  </Typography>
                  <Typography variant="body1" mb={2}>
                    {complaint.reference}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date de création
                  </Typography>
                  <Typography variant="body1" mb={2}>
                    {new Date(complaint.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Statut
                  </Typography>
                  <Chip
                    label={statusLabels[complaint.status]}
                    color={statusColors[complaint.status] as any}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Priorité
                  </Typography>
                  <Chip
                    label={priorityLabels[complaint.priority]}
                    color={priorityColors[complaint.priority] as any}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Sujet
                  </Typography>
                  <Typography variant="body1" mb={2}>
                    {complaint.subject}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1" mb={2}>
                    {complaint.description}
                  </Typography>
                </Grid>
                {complaint.resolution && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Résolution
                    </Typography>
                    <Typography variant="body1" mb={2}>
                      {complaint.resolution}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Notes Section */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes et Suivi
              </Typography>
              
              {/* Add Note */}
              <Box mb={3}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Ajouter une note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <Box mt={1} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || addingNote}
                  >
                    {addingNote ? 'Ajout...' : 'Ajouter une note'}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Notes List */}
              {complaint.notes && complaint.notes.length > 0 ? (
                <List>
                  {complaint.notes.map((note, index) => (
                    <ListItem key={note.id} alignItems="flex-start" divider={index < complaint.notes.length - 1}>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">
                              {note.user.first_name} {note.user.last_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(note.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {note.content}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary" textAlign="center" py={2}>
                  Aucune note disponible
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Customer Info */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations Client
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                Nom
              </Typography>
              <Typography variant="body1" mb={2}>
                {complaint.customer.first_name} {complaint.customer.last_name}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                Email
              </Typography>
              <Typography variant="body1" mb={2}>
                {complaint.customer.email}
              </Typography>
            </CardContent>
          </Card>

          {/* Order Info */}
          {complaint.order && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Commande Associée
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Numéro de commande
                </Typography>
                <Typography variant="body1" mb={2}>
                  {complaint.order.order_number}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Product Info */}
          {complaint.product && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Produit Concerné
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Nom du produit
                </Typography>
                <Typography variant="body1" mb={2}>
                  {complaint.product.name}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Référence
                </Typography>
                <Typography variant="body1">
                  {complaint.product.sku}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplaintDetail;